import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, X, ChevronDown, ChevronUp, AlertTriangle, TrendingUp, Zap, Ban } from 'lucide-react'
import { useAnalyzeStore } from '../../stores/analyzeStore'
import { api } from '../../api/client'

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)
const fmtD = (d: string) => d ? new Date(d + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

type CostView = 'monthly' | 'annual' | 'lifetime'

function usageColor(score: number) {
  if (score >= 70) return 'text-emerald-400 bg-emerald-500/20'
  if (score >= 40) return 'text-amber-400 bg-amber-500/20'
  return 'text-rose-400 bg-rose-500/20'
}

function cancelColor(score: number) {
  if (score >= 60) return 'text-rose-400'
  if (score >= 30) return 'text-amber-400'
  return 'text-slate-500'
}

export function AnalyzePage() {
  const { subscriptions, reviewCount, loading, fetchSubscriptions, fetchReviewItems } = useAnalyzeStore()
  const [dismissing, setDismissing] = useState<string | null>(null)
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null)
  const [costView, setCostView] = useState<CostView>('monthly')

  const toggleExpand = (vendor: string) => setExpandedVendor(prev => prev === vendor ? null : vendor)

  const dismissSub = async (vendor: string) => {
    setDismissing(vendor)
    try {
      await api.post(`/api/subscriptions/${encodeURIComponent(vendor)}/dismiss`)
      fetchSubscriptions()
    } catch (e) { console.error(e) }
    setDismissing(null)
  }

  useEffect(() => {
    fetchSubscriptions()
    fetchReviewItems()
  }, [])

  const active = subscriptions.filter(s => s.status === 'Active')
  const totalMonthly = active.reduce((s, sub) => s + (sub.monthly_cost || 0), 0)
  const priceIncreases = subscriptions.filter(s => s.price_increases?.length > 0)
  const cancelSuggestions = subscriptions.filter(s => s.cancel_score >= 30).sort((a, b) => b.cancel_score - a.cancel_score)
  const lowUsage = subscriptions.filter(s => s.unused_score < 40 && s.status === 'Active')

  const costMultiplier = costView === 'monthly' ? 1 : costView === 'annual' ? 12 : 60
  const costLabel = costView === 'monthly' ? '/mo' : costView === 'annual' ? '/yr' : '/5yr'
  const displayCost = (monthlyCost: number) => fmt(monthlyCost * costMultiplier)

  return (
    <div className="space-y-6">
      {/* Review Queue Banner */}
      {reviewCount > 0 && (
        <Link to="/analyze/review" className="block rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 hover:bg-amber-500/20 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-amber-300">{reviewCount} transactions need your review</p>
              <p className="text-sm text-amber-400/70">Help improve categorization accuracy</p>
            </div>
            <span className="rounded-full bg-amber-500/20 px-3 py-1 text-sm font-medium text-amber-400">Review</span>
          </div>
        </Link>
      )}

      {/* Header + Cost View Toggle */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">Subscription Intelligence</h2>
          <p className="text-xs sm:text-sm text-slate-500">{active.length} active subscriptions detected</p>
        </div>
        <div className="flex gap-1 rounded-lg bg-slate-800 p-1 self-start sm:self-auto">
          {(['monthly', 'annual', 'lifetime'] as CostView[]).map(v => (
            <button key={v} onClick={() => setCostView(v)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors capitalize ${
                costView === v ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-slate-200'
              }`}>{v === 'lifetime' ? '5-Year' : v}</button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-3 sm:p-5">
          <p className="text-xs sm:text-sm text-slate-400">Total Cost</p>
          <p className="text-lg sm:text-2xl font-bold text-white">{displayCost(totalMonthly)}<span className="text-xs sm:text-sm font-normal text-slate-500 ml-1">{costLabel}</span></p>
        </div>
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 sm:p-5">
          <p className="text-xs sm:text-sm text-amber-400/70 flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5 hidden sm:block" /> Price Increases</p>
          <p className="text-lg sm:text-2xl font-bold text-amber-400">{priceIncreases.length}</p>
        </div>
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 sm:p-5">
          <p className="text-xs sm:text-sm text-rose-400/70 flex items-center gap-1"><Ban className="h-3.5 w-3.5 hidden sm:block" /> Cancel Suggestions</p>
          <p className="text-lg sm:text-2xl font-bold text-rose-400">{cancelSuggestions.length}</p>
        </div>
        <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-3 sm:p-5">
          <p className="text-xs sm:text-sm text-indigo-400/70 flex items-center gap-1"><Zap className="h-3.5 w-3.5 hidden sm:block" /> Low Usage</p>
          <p className="text-lg sm:text-2xl font-bold text-indigo-400">{lowUsage.length}</p>
        </div>
      </div>

      {/* ── Cancel Suggestions ── */}
      {cancelSuggestions.length > 0 && (
        <div className="rounded-xl border border-rose-500/20 bg-slate-800/80 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-700/30">
            <h3 className="font-semibold text-rose-300 flex items-center gap-2"><Ban className="h-4 w-4" /> Cancel Suggestions</h3>
            <p className="text-xs text-slate-500 mt-0.5">Subscriptions you might want to reconsider</p>
          </div>
          <div className="divide-y divide-slate-700/20">
            {cancelSuggestions.slice(0, 5).map((sub, i) => (
              <div key={i} className="flex flex-col gap-2 px-4 py-3 hover:bg-slate-800/50 transition-colors sm:flex-row sm:items-center sm:justify-between sm:px-5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-200 truncate">{sub.vendor}</p>
                  <p className="text-xs text-slate-500">
                    {sub.status !== 'Active' && 'Possibly cancelled · '}
                    {sub.unused_score < 40 && 'Low usage · '}
                    {(sub.price_increases?.length ?? 0) > 0 && 'Price increased · '}
                    {displayCost(sub.monthly_cost)}{costLabel}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs font-bold ${cancelColor(sub.cancel_score)}`}>{sub.cancel_score}% cancel</span>
                  <button onClick={() => dismissSub(sub.vendor)} disabled={dismissing === sub.vendor}
                    className="rounded-lg px-3 py-1 text-xs font-medium text-rose-400 border border-rose-500/30 hover:bg-rose-500/10 transition-colors disabled:opacity-50">
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Price Increase Detection ── */}
      {priceIncreases.length > 0 && (
        <div className="rounded-xl border border-amber-500/20 bg-slate-800/80 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-700/30">
            <h3 className="font-semibold text-amber-300 flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Price Increase Detection</h3>
            <p className="text-xs text-slate-500 mt-0.5">Subscriptions that have gotten more expensive</p>
          </div>
          <div className="divide-y divide-slate-700/20">
            {priceIncreases.map((sub, i) => {
              const latest = sub.price_increases[sub.price_increases.length - 1]
              return (
                <div key={i} className="flex flex-col gap-1.5 px-4 py-3 hover:bg-slate-800/50 transition-colors sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-200 truncate">{sub.vendor}</p>
                    <p className="text-xs text-slate-500">{sub.frequency} · {sub.charge_count} charges</p>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 text-xs">
                    <span className="text-slate-500">{fmt(latest.old_amount)}</span>
                    <span className="text-amber-400">→</span>
                    <span className="font-bold text-amber-400">{fmt(latest.new_amount)}</span>
                    <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-amber-300 font-medium">+{latest.pct_change}%</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── All Subscriptions ── */}
      <section>
        <h3 className="mb-3 font-semibold text-white text-lg">All Subscriptions</h3>
        {loading && <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-indigo-500" /></div>}

        <div className="space-y-3">
          {subscriptions.map((sub, i) => {
            const txns = (sub.transactions ?? []) as Array<{ date: string; description: string; amount: number; account?: string }>
            const isExpanded = expandedVendor === sub.vendor
            const hasPriceIncrease = (sub.price_increases?.length ?? 0) > 0
            return (
              <div key={i} className="rounded-xl border border-slate-700/50 bg-slate-800/80 overflow-hidden">
                {/* Summary row */}
                <button onClick={() => toggleExpand(sub.vendor)}
                  className="flex w-full items-center gap-3 p-3 sm:gap-4 sm:p-4 text-left hover:bg-slate-800/50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-200 truncate text-sm">{sub.vendor}</p>
                      {hasPriceIncrease && <span title="Price increased"><TrendingUp className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" /></span>}
                      {sub.cancel_score >= 60 && <span title="Consider cancelling"><AlertTriangle className="h-3.5 w-3.5 text-rose-400 flex-shrink-0" /></span>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-slate-500 truncate">{sub.frequency} · {sub.charge_count} charges{sub.account ? ` · ${sub.account}` : ''}</p>
                      {/* Usage score badge — inline on mobile */}
                      <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold sm:hidden ${usageColor(sub.unused_score)}`}>
                        {sub.unused_score}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    {/* Usage score badge — separate on desktop */}
                    <span className={`hidden sm:inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${usageColor(sub.unused_score)}`}
                      title={`Usage score: ${sub.unused_score}/100`}>
                      {sub.unused_score}
                    </span>
                    <p className="font-bold text-white whitespace-nowrap text-sm">{displayCost(sub.monthly_cost)}<span className="text-xs font-normal text-slate-500">{costLabel}</span></p>
                    <span className={`hidden sm:inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      sub.status === 'Active' ? 'bg-emerald-500/20 text-emerald-400' :
                      sub.status === 'Cancelled' ? 'bg-rose-500/20 text-rose-400' :
                      'bg-slate-700 text-slate-400'
                    }`}>{sub.status}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); dismissSub(sub.vendor) }}
                      disabled={dismissing === sub.vendor}
                      title="Not a subscription"
                      className="hidden sm:block rounded-lg p-1.5 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-slate-700/30 bg-slate-900/40">
                    {/* LLM sub_type badge */}
                    {sub.sub_type && sub.sub_type !== 'other' && (
                      <div className="px-4 py-2 bg-indigo-500/5 border-b border-slate-700/20">
                        <span className="text-xs font-medium text-indigo-300 capitalize">{sub.sub_type}</span>
                        {sub.llm_reasoning && <span className="text-xs text-slate-500 ml-2">· {sub.llm_reasoning}</span>}
                      </div>
                    )}
                    {/* Price increases callout */}
                    {hasPriceIncrease && (
                      <div className="px-4 py-2.5 bg-amber-500/5 border-b border-slate-700/20">
                        <p className="text-xs font-medium text-amber-300 flex items-center gap-1 mb-1"><TrendingUp className="h-3 w-3" /> Price History</p>
                        {sub.price_increases.map((pi, k) => (
                          <p key={k} className="text-xs text-amber-400/70">
                            {fmtD(pi.date)}: {fmt(pi.old_amount)} → {fmt(pi.new_amount)} <span className="text-amber-400 font-medium">(+{pi.pct_change}%)</span>
                          </p>
                        ))}
                      </div>
                    )}
                    {/* Cancellation recommendation */}
                    {sub.cancel_score >= 30 && (
                      <div className={`px-4 py-2.5 border-b border-slate-700/20 ${
                        sub.action_recommendation === 'cancel' ? 'bg-rose-500/5' :
                        sub.action_recommendation === 'review' ? 'bg-amber-500/5' :
                        sub.action_recommendation === 'downgrade' ? 'bg-blue-500/5' : 'bg-slate-500/5'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-medium ${
                            sub.action_recommendation === 'cancel' ? 'text-rose-300' :
                            sub.action_recommendation === 'review' ? 'text-amber-300' :
                            sub.action_recommendation === 'downgrade' ? 'text-blue-300' : 'text-slate-300'
                          }`}>
                            Recommendation: {sub.action_recommendation === 'cancel' ? 'Consider cancelling' : sub.action_recommendation === 'review' ? 'Review needed' : sub.action_recommendation === 'downgrade' ? 'Consider downgrading' : 'Keep'}
                          </span>
                          <span className={`text-xs font-bold ${cancelColor(sub.cancel_score)}`}>{sub.cancel_score}% cancel score</span>
                        </div>
                        {sub.cancel_reasons && sub.cancel_reasons.length > 0 && (
                          <ul className="text-xs text-slate-400 list-disc list-inside">
                            {sub.cancel_reasons.map((reason: string, idx: number) => (
                              <li key={idx}>{reason}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    {/* Alternatives suggestions */}
                    {sub.alternatives && sub.alternatives.length > 0 && (
                      <div className="px-4 py-2.5 bg-emerald-500/5 border-b border-slate-700/20">
                        <p className="text-xs font-medium text-emerald-300 mb-1">Alternatives & Tips</p>
                        <ul className="text-xs text-slate-400 list-disc list-inside">
                          {sub.alternatives.map((alt: string, idx: number) => (
                            <li key={idx}>{alt}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {/* Metadata row */}
                    <div className="px-4 py-2.5 flex flex-wrap gap-4 text-xs text-slate-400 border-b border-slate-700/20">
                      <span>Last: {fmtD(sub.last_charge || '')}</span>
                      <span>Next est: {fmtD(sub.next_estimated || '')}</span>
                      <span>Avg: {fmt(sub.avg_charge)}</span>
                      <span>Last amt: {fmt(sub.last_amount)}</span>
                      <span className={cancelColor(sub.cancel_score)}>Cancel score: {sub.cancel_score}%</span>
                    </div>
                    {/* Transaction list */}
                    {txns.length > 0 ? (
                      <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="text-slate-500">
                            <th className="px-4 py-2 text-left font-medium">Date</th>
                            <th className="px-4 py-2 text-left font-medium">Description</th>
                            <th className="px-4 py-2 text-left font-medium">Account</th>
                            <th className="px-4 py-2 text-right font-medium">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/20">
                          {txns.map((t, j) => (
                            <tr key={j} className="text-slate-400 hover:bg-slate-800/40">
                              <td className="whitespace-nowrap px-4 py-2">{fmtD(t.date)}</td>
                              <td className="px-4 py-2 truncate max-w-xs">{t.description}</td>
                              <td className="px-4 py-2">{t.account || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-2 text-right font-medium text-rose-400">{fmt(Math.abs(t.amount))}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    ) : (
                      <div className="p-3 text-center text-xs text-slate-500">No transactions found</div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          {!loading && subscriptions.length === 0 && (
            <p className="text-center text-slate-500 py-8">No subscriptions detected yet. Sync your accounts first.</p>
          )}
        </div>
      </section>
    </div>
  )
}
